import { useNavigate } from 'react-router-dom';
import QueryString from 'qs';
import { PrimaryColors } from 'src/types/globalTypes';
import DropdownInfo from 'src/components/ui-kit/DropdownInfo';
import { IReqPatentRightsPage, PatentRightsDetail } from 'src/types/api/patentRightsApiTypes';
import {
  SubTitle,
  Table,
  TopItem,
  Item,
  Title,
  Content,
  TagDiv,
  LinkDiv,
  Annotation,
  SearchLink,
} from 'src/components/contents/detail/DetailPage';
import PatentStatusTag from 'src/components/layout/tag/StatusTag';
import { FaLink } from 'react-icons/fa';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from 'src/components/layout/pagination/Pagination';

interface Iprops {
  data: PatentRightsDetail;
  isShow: boolean;
  setIsShow: (isShow: boolean) => void;
  colorType: PrimaryColors;
  noContent: JSX.Element;
}

/**
 * 專利權狀態異動資料 區塊。
 */
const InfoPatentRights: React.FC<Iprops> = ({ data, isShow, setIsShow, colorType, noContent }) => {
  const navigate = useNavigate();
  const tableColor = colorType.third;
  const angleColor = colorType.fifth;
  const linkColor = colorType.seventh;
  const SEPRATOR = ', '; //同格有複數資料的間隔符號
  const ANNOTATION_LINK = <Annotation>※XML及掃描影像檔自公告日期2013/01/01起提供</Annotation>; //連結資訊欄位無資料顯示提醒

  /** 專利人名條件搜尋 */
  const findByName = (condition: string, value: string) => {
    const searchCondition: IReqPatentRightsPage = {
      applyClass: data.applicationReference.applyClass,
      searchName: { keyWord: condition, value: value },
      pageNo: FIRST_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    navigate({
      pathname: process.env.PUBLIC_URL + '/patentRights/result',
      search: QueryString.stringify(searchCondition),
    });
  };

  return (
    <DropdownInfo
      title={`專利權狀態異動資料`}
      isShow={isShow}
      setIsShow={setIsShow}
      color={angleColor}
    >
      <Table color={tableColor}>
        <Item>
          <Title>公告號</Title>
          <Content>{data.publicationReference.publishNo || noContent}</Content>
          <Title>公告日期</Title>
          <Content>{data.publicationReference.publishDate || noContent}</Content>
        </Item>
        <Item>
          <Title>申請案號</Title>
          <Content>{data.applicationReference.applyNo || noContent}</Content>
          <Title>申請日</Title>
          <Content>{data.applicationReference.applyDate || noContent}</Content>
        </Item>
        <Item>
          <Title>設計種類描述</Title>
          <Content>{data.applicationReference.designTypeDesc || noContent}</Content>
          <Title>一案兩請註記</Title>
          <Content>{data.applicationReference.twinsFlag === '0' ? '無' : '有'}</Content>
        </Item>
      </Table>
      <Table color={tableColor}>
        <Item>
          <Title>中文專利名稱</Title>
          <Content>{data.patentTitle.patentNameChinese || noContent}</Content>
          <Title>英文專利名稱</Title>
          <Content>{data.patentTitle.patentNameEnglish || noContent}</Content>
        </Item>
      </Table>
      <Table color={tableColor}>
        <Item>
          <Title>發證日期</Title>
          <Content>{data.patentRight.licenceDate || noContent}</Content>
          <Title>最新補發證日期</Title>
          <Content>{data.patentRight.latestLicenceDate || noContent}</Content>
        </Item>
        <TopItem>
          <Title>登記事項</Title>
          <Content>
            <TagDiv>
              <PatentStatusTag title="授權" code={data.patentRight.rentStatus} />
              <PatentStatusTag title="質權" code={data.patentRight.mortgageStatus} />
              <PatentStatusTag title="讓與" code={data.patentRight.transferStatus} />
              <PatentStatusTag title="繼承" code={data.patentRight.inheritStatus} />
              <PatentStatusTag title="信託" code={data.patentRight.trustStatus} />
              <PatentStatusTag title="舉發" code={data.patentRight.nullityStatus} />
              <PatentStatusTag title="延長" code={data.patentRight.continueStatus} />
            </TagDiv>
          </Content>
        </TopItem>
      </Table>
      <Table color={tableColor}>
        <Item>
          <Title>消滅日期</Title>
          <Content>{data.patentRight.cancelDate || noContent}</Content>
          <Title>消滅原因</Title>
          <Content>{data.patentRight.cancelResult || noContent}</Content>
        </Item>
        <Item>
          <Title>撤銷日期</Title>
          <Content>{data.patentRight.revokeDate || noContent}</Content>
          <Title>撤銷原因</Title>
          <Content>{data.patentRight.revokeResult || noContent}</Content>
        </Item>
        <Item>
          <Title>原案專利類別</Title>
          <Content>{data.patentRight.depPatentClass || noContent}</Content>
          <Title>原案申請案號</Title>
          <Content>{data.patentRight.depApplyNo || noContent}</Content>
        </Item>
      </Table>
      <SubTitle>專利權人資訊</SubTitle>
      {data.applicants.map((item) => {
        return (
          <Table color={tableColor} key={item.sequence}>
            <Item>
              <Title>申請人序號</Title>
              <Content>{item.sequence || noContent}</Content>
              <Title>申請人中文姓名</Title>
              <Content>
                {item.chineseName ? (
                  <SearchLink
                    color={linkColor}
                    onClick={() => {
                      findByName('applnamec', item.chineseName);
                    }}
                  >
                    {item.chineseName}
                  </SearchLink>
                ) : (
                  noContent
                )}
              </Content>
            </Item>
            <Item>
              <Title>申請人英文姓名</Title>
              <Content>
                {item.englishName ? (
                  <SearchLink
                    color={linkColor}
                    onClick={() => {
                      findByName('applnamee', item.englishName);
                    }}
                  >
                    {item.englishName}
                  </SearchLink>
                ) : (
                  noContent
                )}
              </Content>
              <Title>申請人地址</Title>
              <Content>{item.address || noContent}</Content>
            </Item>
            <Item>
              <Title>申請人國籍</Title>
              <Content>{item.chineseCountry || noContent}</Content>
            </Item>
          </Table>
        );
      })}
      <SubTitle>發明人資訊</SubTitle>
      {data.inventors.map((item) => {
        return (
          <Table color={tableColor} key={item.sequence}>
            <Item>
              <Title>發明人序號</Title>
              <Content>{item.sequence || noContent}</Content>
              <Title>發明人中文姓名</Title>
              <Content>
                {item.chineseName ? (
                  <SearchLink
                    color={linkColor}
                    onClick={() => {
                      findByName('inventornamec', item.chineseName);
                    }}
                  >
                    {item.chineseName}
                  </SearchLink>
                ) : (
                  noContent
                )}
              </Content>
            </Item>
            <Item>
              <Title>發明人英文姓名</Title>
              <Content>
                {item.englishName ? (
                  <SearchLink
                    color={linkColor}
                    onClick={() => {
                      findByName('inventornamee', item.englishName);
                    }}
                  >
                    {item.englishName}
                  </SearchLink>
                ) : (
                  noContent
                )}
              </Content>
              <Title>發明人國籍</Title>
              <Content>{item.chineseCountry || noContent}</Content>
            </Item>
          </Table>
        );
      })}
      <SubTitle>代理人資訊</SubTitle>
      {data.agents.map((item) => {
        return (
          <Table color={tableColor} key={item.sequence}>
            <Item>
              <Title>代理人序號</Title>
              <Content>{item.sequence || noContent}</Content>
              <Title>代理人中文姓名</Title>
              <Content>
                {item.chineseName ? (
                  <SearchLink
                    color={linkColor}
                    onClick={() => {
                      findByName('agentnamec', item.chineseName);
                    }}
                  >
                    {item.chineseName}
                  </SearchLink>
                ) : (
                  noContent
                )}
              </Content>
            </Item>
            <Item>
              <Title>代理人中文地址</Title>
              <Content>{item.address || noContent}</Content>
            </Item>
          </Table>
        );
      })}
      <SubTitle>專利分類號</SubTitle>
      <Table color={tableColor}>
        <Item>
          <Title>IPC</Title>
          <Content>
            {(data.ipcs &&
              data.ipcs.map((item, index) => {
                index += 1;
                return index !== data.ipcs.length ? item.ipcFull + SEPRATOR : item.ipcFull;
              })) ||
              noContent}
          </Content>
          <Title>LOC</Title>
          <Content>
            {(data.locs &&
              data.locs.map((item, index) => {
                index += 1;
                return index !== data.locs.length ? item.locFull + SEPRATOR : item.locFull;
              })) ||
              noContent}
          </Content>
        </Item>
      </Table>
      <SubTitle>連結資訊</SubTitle>
      <Table color={tableColor}>
        <Item>
          <Title>專利公告公報書目XML檔</Title>
          <Content>
            {(data.link.patentIsuRegSpecXmlUrl && (
              <LinkDiv color={colorType.seventh} href={data.link.patentIsuRegSpecXmlUrl}>
                <FaLink />
                連結
              </LinkDiv>
            )) || (
              <>
                {noContent}
                {ANNOTATION_LINK}
              </>
            )}
          </Content>
          <Title>專利公告公報書目(多頁式掃描檔)</Title>
          <Content>
            {(data.link.patentIsuRegSpecmTiffUrl && (
              <LinkDiv color={colorType.seventh} href={data.link.patentIsuRegSpecmTiffUrl}>
                <FaLink />
                連結
              </LinkDiv>
            )) || (
              <>
                {noContent}
                {ANNOTATION_LINK}
              </>
            )}
          </Content>
        </Item>
        <Item>
          <Title>專利說明書XML檔</Title>
          <Content>
            {(data.link.patentPubXmlUrl && (
              <LinkDiv color={colorType.seventh} href={data.link.patentPubXmlUrl}>
                <FaLink />
                連結
              </LinkDiv>
            )) || (
              <>
                {noContent}
                {ANNOTATION_LINK}
              </>
            )}
          </Content>
          <Title>專利說明書(多頁式掃描檔)</Title>
          <Content>
            {(data.link.patentPubImgTiffUrl && (
              <LinkDiv color={colorType.seventh} href={data.link.patentPubImgTiffUrl}>
                <FaLink />
                連結
              </LinkDiv>
            )) || (
              <>
                {noContent}
                {ANNOTATION_LINK}
              </>
            )}
          </Content>
        </Item>
      </Table>
    </DropdownInfo>
  );
};

export default InfoPatentRights;
